/**
 * Build-time constants.
 *
 * Supabase config lives **here, not on the preload bridge** (Corrections #17).
 * The renderer owns the session; neither the main process nor the agent ever
 * holds a token. The anon key is also a JWT, and the desktop config is checked
 * by a grep that rejects exactly that shape — so config that belongs to the web
 * stays in the web.
 *
 * `import.meta.env` is inlined by Vite at build time, per environment, exactly
 * as `bimgo-ai-web` does it.
 */

import { AUTH_CONFIRM_PATH } from "./authConfirm";

function required(name: string, value: string | undefined): string {
  if (!value) {
    // Fail loudly and early. A Supabase client built from `undefined` fails much
    // later with an opaque network error, which reads as "login is broken"
    // rather than "this build was not configured".
    throw new Error(
      `${name} is not set. Copy src/web/.env.example to .env.local and fill it in.`,
    );
  }
  return value;
}

export const SUPABASE_URL = (): string =>
  required("VITE_SUPABASE_URL", import.meta.env["VITE_SUPABASE_URL"]);

/**
 * The **publishable/anon** key — never the service key.
 *
 * A service key (`sb_secret_…`, or a JWT with `role: service_role`) bypasses
 * row-level security entirely. In a hosted renderer it would be readable by
 * anyone who opens devtools.
 */
export const SUPABASE_ANON_KEY = (): string =>
  required("VITE_SUPABASE_ANON_KEY", import.meta.env["VITE_SUPABASE_ANON_KEY"]);

export const API_BASE_URL = (): string =>
  required("VITE_API_BASE_URL", import.meta.env["VITE_API_BASE_URL"]);

/**
 * Where GoTrue sends a password-reset link back to.
 *
 * **Derived from the running origin, not from a per-environment constant.** The
 * desktop loads the hosted renderer directly (`BrowserWindow.loadURL(webUrl)`),
 * so the origin already *is* the environment: `https://dev.desktop-interim.bimgo.ai`
 * on dev, `release.` / `app.` on the others, `http://localhost:5173` under
 * `npm run dev`. A build cannot send a link to an environment it is not.
 *
 * It is `/auth/confirm`, the click-to-verify step in front of `/update-password`
 * (`authConfirm.ts`), and it only arrives there if the Supabase **Reset Password
 * email template** builds its link from `{{ .RedirectTo }}`. A template built on
 * `{{ .SiteURL }}` ignores this value entirely and sends every reset to the Site
 * URL — `bimgo-ai-web`'s host on a project the two clients share.
 *
 * Two more things have to be true outside this file for a link to arrive:
 *
 *  - **The URL must be allow-listed in the Supabase project** (Authentication ▸
 *    URL Configuration ▸ Redirect URLs). GoTrue silently falls back to the Site
 *    URL for anything not on the list, which looks exactly like this function
 *    returning the wrong value.
 *  - **The host must serve `index.html` for the path** — see `public/_redirects`.
 *    The link is a cold browser navigation, not a client-side route change.
 */
export const PASSWORD_RESET_REDIRECT = (): string =>
  `${window.location.origin}${AUTH_CONFIRM_PATH}`;

/**
 * Is this page being served from a developer's own machine?
 *
 * **`import.meta.env.DEV` is not the same question**, and conflating them is how
 * a browser gets to a screen only the desktop should show. `DEV` describes how
 * the bundle was *built*; it says nothing about where it is being served from.
 * A development-mode build published to a real hostname carries `DEV === true`
 * with it, and every escape hatch keyed on that alone — the `?preview=` pages,
 * `?smoke-shell=1`, the "let a developer work in Chrome" bypass — would then be
 * open to anyone who visits the URL.
 *
 * So the escape hatches ask both: built for development, **and** running on a
 * loopback host. Everything else meets the desktop gate, whatever the build mode.
 * `/update-password` is the single deliberate exception (#21).
 *
 * The hostname is a parameter so the rule can be tested without a browser.
 */
export function isLocalHost(hostname: string = globalThis.location?.hostname ?? ""): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname === "[::1]" ||
    // Vite also serves `*.localhost` and the `.local` mDNS names a phone on the
    // same network reaches a dev server by.
    hostname.endsWith(".localhost")
  );
}

export function isConfigured(): boolean {
  return Boolean(
    import.meta.env["VITE_SUPABASE_URL"] &&
      import.meta.env["VITE_SUPABASE_ANON_KEY"] &&
      import.meta.env["VITE_API_BASE_URL"],
  );
}
